import { useEffect, useRef, useCallback, useState } from 'react';

/**
 * Global SSE connection — always active.
 * Receives: session_update, memory_update, heartbeat
 * Replaces useRealtimeEvents for global (non-session) events.
 *
 * @param {string} viUserId - Used only to determine whether the user is authenticated
 * @param {{ onSessionUpdate?: function, onMemoryUpdate?: function }} callbacks
 * @returns {{ connected: boolean }}
 */
export function useGlobalEventStream(viUserId, { onSessionUpdate, onMemoryUpdate } = {}) {
  const [connected, setConnected] = useState(false);
  const [reconnectTrigger, setReconnectTrigger] = useState(0);
  const eventSourceRef = useRef(null);
  const reconnectTimerRef = useRef(null);
  const reconnectAttemptRef = useRef(0);
  // Stable refs for callbacks to avoid effect re-runs
  const callbacksRef = useRef({ onSessionUpdate, onMemoryUpdate });
  useEffect(() => { callbacksRef.current = { onSessionUpdate, onMemoryUpdate }; }, [onSessionUpdate, onMemoryUpdate]);

  // Close existing EventSource
  const closeEventSource = useCallback(() => {
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
      eventSourceRef.current = null;
    }
    if (reconnectTimerRef.current) {
      clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = null;
    }
    queueMicrotask(() => setConnected(false));
  }, []);

  useEffect(() => {
    if (!viUserId) {
      closeEventSource();
      return;
    }

    const apiUrl = import.meta.env.VITE_API_URL || '';
    const url = `${apiUrl}/api/users/events`;
    const es = new EventSource(url, { withCredentials: true });
    eventSourceRef.current = es;

    es.onopen = () => {
      reconnectAttemptRef.current = 0;
      setConnected(true);
      console.log('[SSE:global] connected');
    };

    es.addEventListener('session_update', (e) => {
      console.log('[SSE:global] \u2190 session_update');
      try { callbacksRef.current.onSessionUpdate?.(JSON.parse(e.data)); } catch { /* parse error — skip */ }
    });

    es.addEventListener('memory_update', (e) => {
      console.log('[SSE:global] \u2190 memory_update');
      try { callbacksRef.current.onMemoryUpdate?.(JSON.parse(e.data)); } catch { /* parse error — skip */ }
    });

    // Heartbeat — confirms connection is alive, no action needed
    es.addEventListener('heartbeat', () => {
      console.log('[SSE:global] \u2665');
    });

    es.onerror = () => {
      console.log('[SSE:global] error/reconnecting');
      es.close();
      eventSourceRef.current = null;
      setConnected(false);
      // Auto-reconnect with exponential backoff
      const delay = Math.min(1000 * Math.pow(2, reconnectAttemptRef.current), 30000);
      reconnectTimerRef.current = setTimeout(() => {
        reconnectAttemptRef.current++;
        setReconnectTrigger(prev => prev + 1);
      }, delay);
    };

    return () => {
      es.close();
      eventSourceRef.current = null;
      setConnected(false);
    };
  }, [viUserId, closeEventSource, reconnectTrigger]);

  // Cleanup on unmount
  useEffect(() => {
    return () => closeEventSource();
  }, [closeEventSource]);

  return { connected };
}
